import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  TimePicker,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Row,
  Col,
  Spin,
  Drawer,
  Radio,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SettingOutlined,
  UserOutlined,
  DollarOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  CloseCircleOutlined,
  UserAddOutlined,
  CheckCircleFilled,
  WarningFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import './turnosClub.css';

/** Oscurece un color #RRGGBB (mismo matiz, un poco más profundo) para turnos fijos. */
const oscurecerColorDeporte = (hex, factor = 0.78) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex;
  const n = hex.replace('#', '');
  if (n.length !== 6) return hex;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  if ([r, g, b].some((x) => Number.isNaN(x))) return hex;
  const d = (c) => Math.max(0, Math.min(255, Math.round(c * factor)));
  return `#${d(r).toString(16).padStart(2, '0')}${d(g).toString(16).padStart(2, '0')}${d(b).toString(16).padStart(2, '0')}`;
};

const TurnosClub = () => {
  const [turnos, setTurnos] = useState([]);
  const [canchas, setCanchas] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [modalTurnoVisible, setModalTurnoVisible] = useState(false);
  const [modalReservaVisible, setModalReservaVisible] = useState(false);
  const [modalConfigVisible, setModalConfigVisible] = useState(false);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [editingTurno, setEditingTurno] = useState(null);
  const [celdaSeleccionada, setCeldaSeleccionada] = useState(null); // { canchaId, horario }
  const [formTurno] = Form.useForm();
  const [formReserva] = Form.useForm();
  const [formConfig] = Form.useForm();
  const [personaSeleccionadaReserva, setPersonaSeleccionadaReserva] = useState(null); // { tipo: 'jugador'|'alumno', id, nombreCompleto, telefono, email }
  const [opcionesPersonas, setOpcionesPersonas] = useState([]);
  const [buscandoPersonas, setBuscandoPersonas] = useState(false);
  const [busquedaPersonaValor, setBusquedaPersonaValor] = useState('');
  const [modalBuscarPersonaVisible, setModalBuscarPersonaVisible] = useState(false);
  const [modoSinUsuario, setModoSinUsuario] = useState(false);
  const [paginaPersonas, setPaginaPersonas] = useState(1);
  const [guardandoTurno, setGuardandoTurno] = useState(false);
  const [guardandoReserva, setGuardandoReserva] = useState(false);
  const PERSONAS_POR_PAGINA = 5;
  const [tiposDeporte, setTiposDeporte] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [tableWidth, setTableWidth] = useState(0);
  const tableWrapperRef = useRef(null);

  // Configuración de la grilla
  const [configGrilla, setConfigGrilla] = useState(() => {
    const saved = localStorage.getItem('configGrillaClub');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Si hay error al parsear, usar valores por defecto
      }
    }
    return {
      horaDesde: '08:00',
      horaHasta: '22:00',
      duracionTurno: 90, // fallback, se recalcula con las duraciones por deporte
    };
  });

  // Guardar configuración en localStorage
  useEffect(() => {
    localStorage.setItem('configGrillaClub', JSON.stringify(configGrilla));
  }, [configGrilla]);

  // Calcular el MCD (GCD) de dos números
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

  // Calcular el intervalo base de la grilla a partir de las duraciones de los deportes
  const calcularIntervaloBase = () => {
    if (canchas.length === 0) return configGrilla.duracionTurno || 90;
    
    const duraciones = [...new Set(
      canchas
        .map(c => Number(c.tipo_deporte_duracion) || 90)
        .filter(d => d > 0)
    )];
    
    if (duraciones.length === 0) return configGrilla.duracionTurno || 90;
    if (duraciones.length === 1) return duraciones[0];
    
    // Calcular el GCD de todas las duraciones para que los turnos se alineen
    return duraciones.reduce((acc, val) => gcd(acc, val));
  };

  const intervaloBase = calcularIntervaloBase();

  // Obtener la duración del deporte de una cancha específica
  const getDuracionCancha = (canchaId) => {
    const cancha = canchas.find(c => c.ccl_id === canchaId);
    return Number(cancha?.tipo_deporte_duracion) || 90;
  };

  // Generar horarios basados en la configuración
  const generarHorarios = () => {
    const horarios = [];
    const [horaDesde, minDesde] = configGrilla.horaDesde.split(':').map(Number);
    const [horaHasta, minHasta] = configGrilla.horaHasta.split(':').map(Number);
    const duracionMinutos = intervaloBase;

    const inicioMinutos = horaDesde * 60 + minDesde;
    const finMinutos = horaHasta * 60 + minHasta;
    let actualMinutos = inicioMinutos;

    while (actualMinutos < finMinutos) {
      const horas = Math.floor(actualMinutos / 60);
      const minutos = actualMinutos % 60;
      horarios.push(`${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`);
      actualMinutos += duracionMinutos;
    }

    return horarios;
  };

  const horarios = generarHorarios();

  // Cargar configuración de grilla desde el backend (para que jugador y admin usen la misma)
  const loadConfigGrilla = async () => {
    try {
      const response = await apiClient.get('/club/config-grilla');
      if (response.ok) {
        const data = await response.json();
        setConfigGrilla(prev => ({
          ...prev,
          horaDesde: data.horaDesde || '12:00',
          horaHasta: data.horaHasta || '22:00',
          duracionTurno: data.duracionMinutos ?? prev.duracionTurno ?? 90, // Fallback hasta que carguen las canchas
        }));
      }
    } catch (error) {
      console.error('Error al cargar configuración de grilla:', error);
    }
  };

  // Cargar tipos de deporte
  const loadTiposDeporte = async () => {
    try {
      const response = await apiClient.get('/club/tipos-deporte/activos');
      if (response.ok) {
        const data = await response.json();
        setTiposDeporte(data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de deporte:', error);
    }
  };

  // Actualizar duración de un tipo de deporte
  const handleUpdateDuracionDeporte = async (tipoId, nuevaDuracion) => {
    try {
      const response = await apiClient.put(`/club/tipos-deporte/${tipoId}`, {
        duracionMinutos: nuevaDuracion,
      });
      if (response.ok) {
        message.success('Duración actualizada');
        // Recargar tipos de deporte y canchas para refrescar la grilla
        await Promise.all([loadTiposDeporte(), loadCanchas()]);
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al actualizar la duración');
      }
    } catch (error) {
      console.error('Error al actualizar duración:', error);
      message.error('Error al actualizar la duración');
    }
  };

  // Orden fijo de deportes
  const ORDEN_DEPORTES = ['pádel', 'padel', 'picketball', 'fútbol', 'futbol', 'tenis'];

  // Cargar canchas activas (agrupadas por deporte en orden fijo)
  const loadCanchas = async () => {
    try {
      const response = await apiClient.get('/club/canchas/activas');
      if (response.ok) {
        const data = await response.json();
        // Ordenar canchas según orden fijo de deportes
        const ordenadas = [...data].sort((a, b) => {
          const deporteA = (a.tipo_deporte_nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const deporteB = (b.tipo_deporte_nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const idxA = ORDEN_DEPORTES.findIndex(d => deporteA.includes(d));
          const idxB = ORDEN_DEPORTES.findIndex(d => deporteB.includes(d));
          const posA = idxA >= 0 ? idxA : 999;
          const posB = idxB >= 0 ? idxB : 999;
          if (posA !== posB) return posA - posB;
          return (a.ccl_nombre || '').localeCompare(b.ccl_nombre || '');
        });
        setCanchas(ordenadas);
      }
    } catch (error) {
      console.error('Error al cargar canchas:', error);
    }
  };

  // Cargar turnos para todas las canchas en la fecha seleccionada
  const loadTurnos = async () => {
    if (canchas.length === 0) return;
    
    setLoading(true);
    try {
      const fecha = fechaSeleccionada.format('YYYY-MM-DD');
      const promesas = canchas.map(cancha =>
        apiClient.get(`/club/turnos/cancha/${cancha.ccl_id}/fecha/${fecha}`)
      );

      const respuestas = await Promise.all(promesas);
      const todosLosTurnos = [];

      for (const response of respuestas) {
        if (response.ok) {
          const data = await response.json();
          todosLosTurnos.push(...data);
        }
      }

      setTurnos(todosLosTurnos);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los turnos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigGrilla();
    loadCanchas();
    loadTiposDeporte();
  }, []);

  useEffect(() => {
    loadTurnos();
  }, [canchas, fechaSeleccionada]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (tableWrapperRef.current) {
        setTableWidth(tableWrapperRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Obtener turnos por cancha y fecha (priorizando no-DISPONIBLE sobre DISPONIBLE)
  const getTurnosPorCancha = (canchaId) => {
    const filtrados = turnos.filter(turno => Number(turno.tcl_cancha_club) === Number(canchaId));
    // Ordenar: clases/reservados/bloqueados primero, disponibles al final
    return filtrados.sort((a, b) => {
      const prioridadEstado = (t) => {
        if (t.es_clase_academia || t.tcl_tipo === 'CLASE_ACADEMIA') return 0;
        if (t.tcl_estado === 'RESERVADO') return 1;
        if (t.tcl_estado === 'BLOQUEADO') return 2;
        return 3; // DISPONIBLE
      };
      return prioridadEstado(a) - prioridadEstado(b);
    });
  };

  // Verificar si un horario está dentro de un turno
  const estaEnTurno = (horario, turno) => {
    const [hora, minuto] = horario.split(':').map(Number);
    const [desdeH, desdeM] = turno.tcl_hora_desde.split(':').map(Number);
    const [hastaH, hastaM] = turno.tcl_hora_hasta.split(':').map(Number);
    
    const horarioMinutos = hora * 60 + minuto;
    const desdeMinutos = desdeH * 60 + desdeM;
    const hastaMinutos = hastaH * 60 + hastaM;
    
    return horarioMinutos >= desdeMinutos && horarioMinutos < hastaMinutos;
  };

  // Verificar si un horario es el inicio de un turno
  const esInicioTurno = (horario, turno) => {
    if (!turno || !turno.tcl_hora_desde) {
      return false;
    }
    
    const [hora, minuto] = horario.split(':').map(Number);
    const horaDesdeParts = turno.tcl_hora_desde.split(':');
    const desdeH = Number(horaDesdeParts[0]);
    const desdeM = Number(horaDesdeParts[1]);
    
    const horarioMinutos = hora * 60 + minuto;
    const desdeMinutos = desdeH * 60 + desdeM;
    
    // El turno debe comenzar en este slot exacto o dentro de los primeros minutos del intervalo
    const diferencia = desdeMinutos - horarioMinutos;
    
    return diferencia >= 0 && diferencia < intervaloBase;
  };

  // Calcular cuántos slots ocupa un turno
  const calcularSlotsTurno = (turno) => {
    const [desdeH, desdeM] = turno.tcl_hora_desde.split(':').map(Number);
    const [hastaH, hastaM] = turno.tcl_hora_hasta.split(':').map(Number);
    
    const desdeMinutos = desdeH * 60 + desdeM;
    const hastaMinutos = hastaH * 60 + hastaM;
    const duracionMinutos = hastaMinutos - desdeMinutos;
    
    return Math.ceil(duracionMinutos / intervaloBase);
  };

  // Precalcular el layout de cada cancha: para cada horario, cuántos slots debe ocupar
  // Esto recorre los horarios secuencialmente, lo que evita bugs de alineación
  const calcularLayoutCancha = (canchaId) => {
    const turnosEnCancha = getTurnosPorCancha(canchaId);
    const duracionDeporte = getDuracionCancha(canchaId);
    const slotsDeporte = Math.max(Math.ceil(duracionDeporte / intervaloBase), 1);
    const layout = {}; // { horario: rowSpan }
    
    let skip = 0; // cuántos slots seguir ocultando
    
    for (let i = 0; i < horarios.length; i++) {
      const horario = horarios[i];
      
      // Si estamos dentro de un bloque anterior (turno o vacío agrupado), ocultar
      if (skip > 0) {
        layout[horario] = 0;
        skip--;
        continue;
      }
      
      // Verificar si hay un turno que empieza en este slot
      const turno = turnosEnCancha.find(t => esInicioTurno(horario, t));
      if (turno) {
        const slotsTurno = calcularSlotsTurno(turno);
        layout[horario] = slotsTurno;
        skip = slotsTurno - 1;
        continue;
      }
      
      // Verificar si este slot está dentro de un turno que empezó antes
      const dentroTurno = turnosEnCancha.some(t => {
        if (esInicioTurno(horario, t)) return false;
        return estaEnTurno(horario, t);
      });
      if (dentroTurno) {
        layout[horario] = 0;
        continue;
      }
      
      // Celda vacía: agrupar según la duración del deporte
      // Los bloques disponibles solo pueden empezar en límites alineados con la
      // duración del deporte (ej: si Picketball = 1h y la grilla arranca a las 7:00,
      // los turnos solo pueden arrancar a las 7:00, 8:00, 9:00, etc.)
      const [_hh, _mm] = horario.split(':').map(Number);
      const horarioMin = _hh * 60 + _mm;
      const [_sh, _sm] = horarios[0].split(':').map(Number);
      const gridStartMin = _sh * 60 + _sm;
      const estaAlineado = duracionDeporte > 0 && (horarioMin - gridStartMin) % duracionDeporte === 0;

      if (!estaAlineado) {
        // Este slot no está en un límite alineado → relleno hasta el próximo límite
        const siguienteAlineado = gridStartMin + Math.ceil((horarioMin - gridStartMin) / duracionDeporte) * duracionDeporte;
        const slotsHastaAlineado = Math.ceil((siguienteAlineado - horarioMin) / intervaloBase);

        let slotsRellenoNA = 0;
        for (let j = 0; j < slotsHastaAlineado && (i + j) < horarios.length; j++) {
          const h = horarios[i + j];
          const hayTurno = turnosEnCancha.some(t => estaEnTurno(h, t));
          if (hayTurno) break;
          slotsRellenoNA++;
        }
        if (slotsRellenoNA > 0) {
          layout[horarios[i]] = -slotsRellenoNA;
          for (let j = 1; j < slotsRellenoNA; j++) {
            layout[horarios[i + j]] = 0;
          }
          skip = slotsRellenoNA - 1;
        }
        continue;
      }

      // Slot alineado: contar cuántos slots vacíos consecutivos hay (máximo = slots del deporte)
      let slotsVacios = 0;
      for (let j = 0; j < slotsDeporte && (i + j) < horarios.length; j++) {
        const siguienteHorario = horarios[i + j];
        const hayTurno = turnosEnCancha.some(t => estaEnTurno(siguienteHorario, t));
        if (hayTurno) break;
        slotsVacios++;
      }
      
      // Si no caben suficientes slots para un turno completo del deporte,
      // no mostrar como bloque disponible (celdas individuales de relleno)
      if (slotsVacios < slotsDeporte) {
        // Marcar como relleno: valor negativo = relleno, el absoluto = rowSpan real
        layout[horarios[i]] = -slotsVacios;
        for (let j = 1; j < slotsVacios && (i + j) < horarios.length; j++) {
          layout[horarios[i + j]] = 0;
        }
        skip = slotsVacios - 1;
      } else {
        layout[horario] = slotsVacios;
        skip = slotsVacios - 1;
      }
    }
    
    return layout;
  };
  
  // Cache del layout por cancha (se recalcula cuando cambian turnos, canchas u horarios)
  const layoutsPorCancha = React.useMemo(() => {
    const layouts = {};
    for (const cancha of canchas) {
      layouts[cancha.ccl_id] = calcularLayoutCancha(cancha.ccl_id);
    }
    return layouts;
  }, [turnos, canchas, horarios, intervaloBase]);

  // Renderizar bloque de turno como cuadrado (RESERVADO o BLOQUEADO). DISPONIBLE no se muestra: celda vacía.
  const renderBloqueTurno = (canchaId, horario, colorDeporte) => {
    const turnosEnCancha = getTurnosPorCancha(canchaId);
    const turno = turnosEnCancha.find(t => esInicioTurno(horario, t));
    
    if (!turno) {
      return null;
    }
    // Turnos DISPONIBLE no muestran cartel: la celda queda vacía (el admin puede hacer clic para crear reserva)
    if (turno.tcl_estado === 'DISPONIBLE') {
      return null;
    }
    // Rellenos de clase son invisibles: solo ocupan espacio para evitar slots huérfanos
    if (turno.es_relleno_clase || turno.tcl_tipo === 'RELLENO_CLASE') {
      return null;
    }

    const slotsOcupados = calcularSlotsTurno(turno);
    const baseSlotHeight = isMobile ? 60 : 80;
    const alturaBloque = slotsOcupados * baseSlotHeight;

    // Determinar color y estilo según estado
    const esReservado = turno.tcl_estado === 'RESERVADO';
    const esBloqueado = turno.tcl_estado === 'BLOQUEADO';
    const esDisponible = turno.tcl_estado === 'DISPONIBLE';
    const esFijo = turno.tcl_tipo === 'FIJO';
    const esClaseAcademia = turno.es_clase_academia || turno.tcl_tipo === 'CLASE_ACADEMIA';

    // Colores basados en el deporte de la cancha
    const deporteColor = colorDeporte || { bg: '#1a2332', border: '#1e3a5f', text: '#5b9bd5' };

    const getBackgroundColor = () => {
      if (esBloqueado && !esClaseAcademia) {
        return '#ff4d4f'; // Rojo solo para bloqueado manual
      }
      const base = deporteColor.text;
      return esFijo ? oscurecerColorDeporte(base, 0.78) : base;
    };

    const getBorderColor = () => {
      if (esBloqueado && !esClaseAcademia) {
        return '#cf1322';
      }
      const base = deporteColor.border;
      return esFijo ? oscurecerColorDeporte(base, 0.72) : base;
    };

    const precioNum = typeof turno.tcl_precio === 'string' 
      ? parseFloat(turno.tcl_precio) 
      : turno.tcl_precio;

    // Días de la semana para mostrar en turnos fijos
    const diasSemanaNombres = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    return (
      <div
        key={turno.tcl_id}
        style={{
          position: 'absolute',
          top: '2px',
          left: '2px',
          right: '2px',
          bottom: '2px',
          height: isMobile ? `${alturaBloque - 4}px` : 'auto',
          background: getBackgroundColor(),
          color: '#fff',
          border: `2px solid ${getBorderColor()}`,
          borderRadius: '6px',
          padding: isMobile ? '8px' : '10px',
          fontSize: isMobile ? '11px' : '12px',
          cursor: 'pointer',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        }}
        onClick={() => {
          // Las clases de academia no se pueden editar desde el módulo del club
          if (esClaseAcademia) {
            message.info('Esta es una clase de academia. Para editarla, ve al módulo de Academia.');
            return;
          }
          if (esDisponible) {
            handleCrearReserva(turno);
          } else {
            handleEditTurno(turno);
          }
        }}
      >
        {/* Indicador de turno fijo en la esquina superior derecha */}
        {esFijo && (
          <div style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: isMobile ? '8px' : '9px',
            fontWeight: '600',
            padding: '2px 6px',
            borderRadius: '3px',
            textTransform: 'uppercase',
            zIndex: 11,
            lineHeight: '1.2',
          }}>
            Fijo
          </div>
        )}
        {/* Si está reservado, mostrar solo nombre y teléfono */}
        {esReservado && turno.rcl_nombre_cliente ? (
          <>
            {!esFijo && turno.rcl_estado_pago && (
              <div style={{ marginBottom: 4, textAlign: 'center' }}>
                {(() => {
                  const estadoPago = String(turno.rcl_estado_pago || '').toLowerCase();
                  if (estadoPago === 'pagado') {
                    return <Tag color="green" style={{ fontSize: isMobile ? '10px' : '11px', margin: 0 }}>Pagado</Tag>;
                  }
                  if (estadoPago === 'pendiente') {
                    return <Tag color="orange" style={{ fontSize: isMobile ? '10px' : '11px', margin: 0 }}>Pendiente</Tag>;
                  }
                  return <Tag style={{ fontSize: isMobile ? '10px' : '11px', margin: 0 }}>Sin seña</Tag>;
                })()}
              </div>
            )}
            <div style={{ 
              fontSize: isMobile ? '13px' : '15px', 
              fontWeight: '600',
              marginBottom: turno.rcl_telefono ? '6px' : '0',
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: '1.4'
            }}>
              {turno.rcl_nombre_cliente}
            </div>
            {turno.rcl_telefono && (
              <div style={{ 
                fontSize: isMobile ? '11px' : '13px', 
                opacity: 0.85,
                textAlign: 'center',
                width: '100%',
                lineHeight: '1.3'
              }}>
                {turno.rcl_telefono}
              </div>
            )}
          </>
        ) : esDisponible ? (
          <>
            {/* Horario - solo para turnos disponibles */}
            <div style={{ 
              fontWeight: 'bold', 
              fontSize: isMobile ? '12px' : '13px', 
              marginBottom: '6px',
              textAlign: 'center',
              width: '100%'
            }}>
              {turno.tcl_hora_desde.substring(0, 5)} - {turno.tcl_hora_hasta.substring(0, 5)}
            </div>

            {/* Indicador de turno fijo */}
            {esFijo && turno.tcl_dia_semana !== null && turno.tcl_dia_semana !== undefined && (
              <div style={{ 
                fontSize: isMobile ? '10px' : '11px', 
                marginBottom: '6px',
                opacity: 0.9,
                textAlign: 'center',
                width: '100%',
                fontWeight: '500'
              }}>
                {diasSemanaNombres[turno.tcl_dia_semana]}
              </div>
            )}
            {/* Si está disponible, mostrar precio */}
            <div style={{ 
              fontSize: isMobile ? '15px' : '17px', 
              fontWeight: 'bold',
              marginBottom: '6px',
              textAlign: 'center',
              width: '100%'
            }}>
              ${precioNum ? precioNum.toFixed(2) : '0.00'}
            </div>
            <div style={{ 
              fontSize: isMobile ? '11px' : '13px', 
              opacity: 0.9,
              textAlign: 'center',
              width: '100%',
              textTransform: 'uppercase',
              fontWeight: '500'
            }}>
              Disponible
            </div>
          </>
        ) : esClaseAcademia ? (
          <>
            {/* Si es una clase de academia */}
            <div style={{ 
              fontSize: isMobile ? '10px' : '11px', 
              fontWeight: '600',
              textAlign: 'center',
              width: '100%',
              textTransform: 'uppercase',
              marginBottom: '4px',
              opacity: 0.9
            }}>
              CLASE
            </div>
            <div style={{ 
              fontSize: isMobile ? '13px' : '15px', 
              fontWeight: '600',
              textAlign: 'center',
              width: '100%',
              marginBottom: turno.clase_profesor ? '4px' : '0'
            }}>
              {turno.clase_categoria || 'Academia'}
            </div>
            {turno.clase_profesor && (
              <div style={{ 
                fontSize: isMobile ? '11px' : '12px', 
                opacity: 0.85,
                textAlign: 'center',
                width: '100%'
              }}>
                {turno.clase_profesor}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Si está bloqueado */}
            <div style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              fontWeight: '600',
              textAlign: 'center',
              width: '100%',
              textTransform: 'uppercase'
            }}>
              Bloqueado
            </div>
          </>
        )}
      </div>
    );
  };

  // Abrir modal para crear nuevo turno desde celda vacía
  const handleCrearTurnoDesdeCelda = (canchaId, horario) => {
    if (!canchaId || !horario) return;

    // Calcular hora hasta basada en la duración del deporte de la cancha
    const duracionDeporte = getDuracionCancha(canchaId);
    const [hora, minuto] = horario.split(':').map(Number);
    const inicioMinutos = hora * 60 + minuto;
    const finMinutos = inicioMinutos + duracionDeporte;
    const horasFin = Math.floor(finMinutos / 60);
    const minutosFin = finMinutos % 60;
    const horaHasta = `${horasFin.toString().padStart(2, '0')}:${minutosFin.toString().padStart(2, '0')}`;

    // Verificar solapamiento con turnos existentes en esa cancha
    const turnosEnCancha = getTurnosPorCancha(canchaId);
    const haySolapamiento = turnosEnCancha.some(t => {
      const tDesde = (t.tcl_hora_desde || '').substring(0, 5);
      const tHasta = (t.tcl_hora_hasta || '').substring(0, 5);
      return horario < tHasta && horaHasta > tDesde;
    });
    if (haySolapamiento) {
      message.warning('No se puede crear un turno aquí, se superpone con un turno o clase existente');
      return;
    }

    // Obtener precio de la franja horaria si existe
    const obtenerPrecioFranja = async () => {
      try {
        const diaSemana = fechaSeleccionada.day(); // 0=Dom, 1=Lun, ... 6=Sab
        const response = await apiClient.get(`/club/franjas-horarias/cancha/${canchaId}/activas`);
        if (response.ok) {
          const franjas = await response.json();
          // Normalizar hora a "HH:mm" para comparar igual aunque la API devuelva "07:00" o "07:00:00"
          const normHora = (h) => (h ? String(h).substring(0, 5) : '');
          const slotHora = normHora(horario);
          const franjaDelDia = franjas.find(f => {
            const diaOk = Number(f.fho_dia_semana) === diaSemana;
            const desde = normHora(f.fho_hora_desde);
            const hasta = normHora(f.fho_hora_hasta);
            const dentro = desde <= slotHora && hasta > slotHora;
            return diaOk && dentro;
          });
          if (franjaDelDia) {
            const precioNum = typeof franjaDelDia.fho_precio === 'string'
              ? parseFloat(franjaDelDia.fho_precio)
              : franjaDelDia.fho_precio;
            return precioNum ?? 0;
          }
          // Fallback: misma cancha y mismo día, usar la primera franja del día
          const primeraFranjaDia = franjas.find(f => Number(f.fho_dia_semana) === diaSemana);
          if (primeraFranjaDia) {
            const p = typeof primeraFranjaDia.fho_precio === 'string'
              ? parseFloat(primeraFranjaDia.fho_precio)
              : primeraFranjaDia.fho_precio;
            return p ?? 0;
          }
          // Último fallback: cualquier franja de la cancha
          if (franjas.length > 0) {
            const p = typeof franjas[0].fho_precio === 'string'
              ? parseFloat(franjas[0].fho_precio)
              : franjas[0].fho_precio;
            return p ?? 0;
          }
        }
      } catch (error) {
        console.error('Error al obtener precio de franja:', error);
      }
      return 0;
    };

    setCeldaSeleccionada({ canchaId, horario });
    setEditingTurno(null);
    formTurno.resetFields();
    
    obtenerPrecioFranja().then(precio => {
      formTurno.setFieldsValue({
        canchaClub: canchaId,
        tipo: 'UNICO',
        fecha: fechaSeleccionada,
        horaDesde: dayjs(horario, 'HH:mm'),
        horaHasta: dayjs(horaHasta, 'HH:mm'),
        precio: precio,
        estado: 'DISPONIBLE',
        // Mostrar modal de reserva directamente para asignar responsable
      });
      // Abrir modal de reserva directamente para asignar responsable
      setTurnoSeleccionado({
        tcl_cancha_club: canchaId,
        tcl_fecha: fechaSeleccionada.format('YYYY-MM-DD'),
        tcl_hora_desde: horario + ':00',
        tcl_hora_hasta: horaHasta + ':00',
        tcl_precio: precio,
        tcl_estado: 'DISPONIBLE',
        esNuevo: true, // Flag para indicar que es un turno nuevo
      });
      formReserva.resetFields();
      // Obtener el día de la semana de la fecha seleccionada (0=Domingo, 1=Lunes, etc.)
      const diaSemana = fechaSeleccionada.day();
      formReserva.setFieldsValue({
        tipoTurno: 'UNICO', // Por defecto es Único
        diaSemana: diaSemana, // Pre-seleccionar el día de la semana para cuando cambien a Fijo
      });
      setPersonaSeleccionadaReserva(null);
      setBusquedaPersonaValor('');
      setOpcionesPersonas([]);
      setModalReservaVisible(true);
    });
  };

  // Abrir modal para crear nuevo turno manual
  const handleCreateTurno = () => {
    setEditingTurno(null);
    setCeldaSeleccionada(null);
    formTurno.resetFields();
    formTurno.setFieldsValue({
      tipo: 'UNICO',
      fecha: fechaSeleccionada,
      estado: 'DISPONIBLE',
    });
    setModalTurnoVisible(true);
  };

  // Abrir modal para editar turno
  const handleEditTurno = (record) => {
    setEditingTurno(record);
    setCeldaSeleccionada(null);
    // Convertir precio a número si viene como string
    const precioNum = typeof record.tcl_precio === 'string' 
      ? parseFloat(record.tcl_precio) 
      : record.tcl_precio;
    
    const tipo = record.tcl_tipo || 'UNICO';
    
    formTurno.setFieldsValue({
      canchaClub: record.tcl_cancha_club,
      tipo: tipo,
      diaSemana: record.tcl_dia_semana !== null && record.tcl_dia_semana !== undefined ? record.tcl_dia_semana : undefined,
      fecha: record.tcl_fecha ? dayjs(record.tcl_fecha) : fechaSeleccionada,
      horaDesde: dayjs(record.tcl_hora_desde, 'HH:mm:ss'),
      horaHasta: dayjs(record.tcl_hora_hasta, 'HH:mm:ss'),
      precio: precioNum || 0,
      estado: record.tcl_estado,
      observaciones: record.tcl_observaciones,
    });
    setModalTurnoVisible(true);
  };

  // Marcar turno fijo como "NO ASISTE" para la fecha seleccionada
  const handleNoAsiste = async (turno) => {
    if (turno.tcl_tipo !== 'FIJO') {
      message.warning('Esta función solo está disponible para turnos fijos');
      return;
    }

    try {
      const fecha = fechaSeleccionada.format('YYYY-MM-DD');
      const response = await apiClient.post(`/club/turnos/${turno.tcl_id}/excepcion`, { fecha });
      
      if (response.ok) {
        message.success('El turno fijo ha sido liberado para esta fecha');
        setModalTurnoVisible(false);
        setEditingTurno(null);
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al liberar el turno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al liberar el turno');
    }
  };

  // Guardar turno (crear o actualizar)
  const handleSaveTurno = async () => {
    if (guardandoTurno) return;
    setGuardandoTurno(true);
    try {
      const values = await formTurno.validateFields();
      const horaDesde = values.horaDesde.format('HH:mm:ss');
      const horaHasta = values.horaHasta.format('HH:mm:ss');
      const tipo = values.tipo || 'UNICO';
      
      const data = {
        canchaClub: values.canchaClub,
        tipo: tipo,
        horaDesde,
        horaHasta,
        precio: values.precio,
        estado: values.estado,
        observaciones: values.observaciones,
      };

      // Agregar campos según el tipo
      if (tipo === 'FIJO') {
        data.diaSemana = values.diaSemana;
      } else {
        data.fecha = values.fecha.format('YYYY-MM-DD');
      }

      if (editingTurno) {
        // Actualizar
        const response = await apiClient.put(`/club/turnos/${editingTurno.tcl_id}`, data);
        if (response.ok) {
          message.success('Turno actualizado correctamente');
          setModalTurnoVisible(false);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el turno');
        }
      } else {
        // Crear
        const response = await apiClient.post('/club/turnos', data);
        if (response.ok) {
          message.success('Turno creado correctamente');
          setModalTurnoVisible(false);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el turno');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al guardar el turno');
    } finally {
      setGuardandoTurno(false);
    }
  };

  // Abrir modal para crear reserva
  const handleCrearReserva = (turno) => {
    if (turno.tcl_estado !== 'DISPONIBLE') {
      message.warning('El turno no está disponible para reservar');
      return;
    }
    setTurnoSeleccionado(turno);
    formReserva.resetFields();
    setPersonaSeleccionadaReserva(null);
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    // Convertir precio a número si viene como string
    const precioNum = typeof turno.tcl_precio === 'string' 
      ? parseFloat(turno.tcl_precio) 
      : turno.tcl_precio;
    
    formReserva.setFieldsValue({
      tipoTurno: turno.tcl_tipo || 'UNICO',
      diaSemana: turno.tcl_dia_semana ?? fechaSeleccionada.day(),
    });
    setModalReservaVisible(true);
  };

  const debounceBusquedaPersonasRef = useRef(null);

  // Buscar personas en el modal de selección
  const buscarPersonasReserva = (q) => {
    if (!q || q.trim().length < 2) {
      setOpcionesPersonas([]);
      setPaginaPersonas(1);
      return;
    }
    if (debounceBusquedaPersonasRef.current) clearTimeout(debounceBusquedaPersonasRef.current);
    debounceBusquedaPersonasRef.current = setTimeout(async () => {
      setBuscandoPersonas(true);
      setPaginaPersonas(1);
      try {
        const response = await apiClient.get(`/club/buscar-personas?q=${encodeURIComponent(q.trim())}`);
        if (response.ok) {
          const data = await response.json();
          const merged = [
            ...(data.jugadores || []).map((p) => ({ ...p, tipo: 'jugador' })),
            ...(data.alumnos || []).map((p) => ({ ...p, tipo: 'alumno' })),
          ];
          setOpcionesPersonas(merged);
        } else {
          setOpcionesPersonas([]);
        }
      } catch (err) {
        console.error('Error al buscar personas:', err);
        setOpcionesPersonas([]);
      } finally {
        setBuscandoPersonas(false);
      }
    }, 300);
  };

  // Seleccionar persona desde el modal de búsqueda
  const handleSelectPersonaReserva = (persona) => {
    setPersonaSeleccionadaReserva(persona);
    setModoSinUsuario(false);
    formReserva.setFieldsValue({
      nombreCliente: persona.nombreCompleto,
      telefono: persona.telefono || '',
      email: persona.email || '',
    });
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setModalBuscarPersonaVisible(false);
  };

  // Abrir modal de búsqueda de personas
  const handleAbrirBuscarPersona = () => {
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setPaginaPersonas(1);
    setModalBuscarPersonaVisible(true);
  };

  // Activar modo sin usuario
  const handleAgregarSinUsuario = () => {
    setPersonaSeleccionadaReserva(null);
    setModoSinUsuario(true);
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setModalBuscarPersonaVisible(false);
    formReserva.setFieldsValue({
      nombreCliente: undefined,
      telefono: undefined,
      email: undefined,
    });
  };

  // Quitar persona seleccionada
  const handleQuitarPersona = () => {
    setPersonaSeleccionadaReserva(null);
    setModoSinUsuario(false);
    formReserva.setFieldsValue({
      nombreCliente: undefined,
      telefono: undefined,
      email: undefined,
    });
  };

  // Guardar reserva
  const handleSaveReserva = async () => {
    if (guardandoReserva) return;
    setGuardandoReserva(true);
    try {
      const values = await formReserva.validateFields();
      
      // Si es un turno nuevo (no tiene tcl_id), crear primero el turno y luego la reserva
      if (turnoSeleccionado.esNuevo) {
        // Obtener tipo de turno del formulario
        const tipoTurno = values.tipoTurno || 'UNICO';
        
        // Crear el turno primero
        const turnoData = {
          canchaClub: turnoSeleccionado.tcl_cancha_club,
          tipo: tipoTurno,
          horaDesde: turnoSeleccionado.tcl_hora_desde,
          horaHasta: turnoSeleccionado.tcl_hora_hasta,
          precio: turnoSeleccionado.tcl_precio,
          estado: 'DISPONIBLE', // Se cambiará a RESERVADO cuando se cree la reserva
        };

        // Agregar campos según el tipo
        if (tipoTurno === 'FIJO') {
          turnoData.diaSemana = values.diaSemana;
        } else {
          turnoData.fecha = turnoSeleccionado.tcl_fecha;
        }

        const turnoResponse = await apiClient.post('/club/turnos', turnoData);
        if (!turnoResponse.ok) {
          const error = await turnoResponse.json();
          message.error(error.message || 'Error al crear el turno');
          return;
        }

        const turnoCreado = await turnoResponse.json();
        
        // Ahora crear la reserva con el turno creado
        const reservaData = {
          turnoClub: turnoCreado.tcl_id,
          nombreCliente: values.nombreCliente,
          telefono: values.telefono,
          email: values.email,
          precioPagado: 0,
          observaciones: values.observaciones,
          jugadorClub: personaSeleccionadaReserva?.tipo === 'jugador' ? personaSeleccionadaReserva.id : undefined,
          alumno: personaSeleccionadaReserva?.tipo === 'alumno' ? personaSeleccionadaReserva.id : undefined,
        };

        const reservaResponse = await apiClient.post('/club/reservas', reservaData);
        if (reservaResponse.ok) {
          message.success('Turno creado y reservado correctamente');
          setModalReservaVisible(false);
          setTurnoSeleccionado(null);
          setCeldaSeleccionada(null);
          setPersonaSeleccionadaReserva(null);
          setBusquedaPersonaValor('');
          loadTurnos();
        } else {
          const error = await reservaResponse.json();
          message.error(error.message || 'Error al crear la reserva');
        }
      } else {
        const tipoTurnoSeleccionado = values.tipoTurno || turnoSeleccionado.tcl_tipo || 'UNICO';
        let turnoIdParaReserva = turnoSeleccionado.tcl_id;

        // Si cambian el tipo respecto al turno base, crear un turno nuevo del tipo elegido.
        if (tipoTurnoSeleccionado !== (turnoSeleccionado.tcl_tipo || 'UNICO')) {
          const nuevoTurnoData = {
            canchaClub: turnoSeleccionado.tcl_cancha_club,
            tipo: tipoTurnoSeleccionado,
            horaDesde: turnoSeleccionado.tcl_hora_desde,
            horaHasta: turnoSeleccionado.tcl_hora_hasta,
            precio: turnoSeleccionado.tcl_precio,
            estado: 'DISPONIBLE',
          };

          if (tipoTurnoSeleccionado === 'FIJO') {
            nuevoTurnoData.diaSemana = values.diaSemana ?? fechaSeleccionada.day();
          } else {
            nuevoTurnoData.fecha = fechaSeleccionada.format('YYYY-MM-DD');
          }

          const nuevoTurnoResponse = await apiClient.post('/club/turnos', nuevoTurnoData);
          if (!nuevoTurnoResponse.ok) {
            const error = await nuevoTurnoResponse.json();
            message.error(error.message || 'Error al crear el turno');
            return;
          }

          const nuevoTurno = await nuevoTurnoResponse.json();
          turnoIdParaReserva = nuevoTurno.tcl_id;
        }

        // Turno existente (o nuevo por cambio de tipo): crear la reserva
        const data = {
          turnoClub: turnoIdParaReserva,
          nombreCliente: values.nombreCliente,
          telefono: values.telefono,
          email: values.email,
          precioPagado: 0,
          observaciones: values.observaciones,
          jugadorClub: personaSeleccionadaReserva?.tipo === 'jugador' ? personaSeleccionadaReserva.id : undefined,
          alumno: personaSeleccionadaReserva?.tipo === 'alumno' ? personaSeleccionadaReserva.id : undefined,
        };

        const response = await apiClient.post('/club/reservas', data);
        if (response.ok) {
          message.success('Reserva creada correctamente');
          setModalReservaVisible(false);
          setPersonaSeleccionadaReserva(null);
          setBusquedaPersonaValor('');
          setTurnoSeleccionado(null);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la reserva');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al crear la reserva');
    } finally {
      setGuardandoReserva(false);
    }
  };

  // Eliminar turno
  const handleDeleteTurno = async (id) => {
    try {
      const response = await apiClient.delete(`/club/turnos/${id}`);
      if (response.ok) {
        message.success('Turno eliminado correctamente');
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el turno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el turno');
    }
  };

  // Eliminar reserva
  const handleDeleteReserva = async (turno) => {
    if (!turno.reserva_id) {
      message.warning('Este turno no tiene una reserva asociada');
      return;
    }

    try {
      const response = await apiClient.delete(`/club/reservas/${turno.reserva_id}`);
      if (response.ok) {
        message.success('Reserva eliminada correctamente. El turno ha sido liberado.');
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la reserva');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la reserva');
    }
  };


  // Guardar configuración de grilla (local + backend para que jugador use la misma)
  const handleSaveConfig = async () => {
    try {
      const values = await formConfig.validateFields();
      const horaDesde = values.horaDesde.format('HH:mm');
      const horaHasta = values.horaHasta.format('HH:mm');

      const response = await apiClient.put('/club/config-grilla', {
        horaDesde,
        horaHasta,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Error al guardar en el servidor');
      }

      setConfigGrilla(prev => ({
        ...prev,
        horaDesde,
        horaHasta,
      }));

      message.success('Configuración guardada correctamente');
      setModalConfigVisible(false);
    } catch (error) {
      console.error('Error al guardar:', error);
      if (error.errorFields) {
        return;
      }
      message.error(error.message || 'Error al guardar la configuración');
    }
  };

  // Configurar formulario de configuración con valores actuales
  useEffect(() => {
    if (modalConfigVisible) {
      formConfig.setFieldsValue({
        horaDesde: dayjs(configGrilla.horaDesde, 'HH:mm'),
        horaHasta: dayjs(configGrilla.horaHasta, 'HH:mm'),
      });
    }
  }, [modalConfigVisible, configGrilla]);

  // Columnas de la tabla
  const baseSlotHeight = isMobile ? 60 : 80;
  const horarioColumnWidth = isMobile ? 55 : 90;
  const canchasCount = Math.max(canchas.length, 1);
  let canchaColumnWidth = isMobile ? 110 : 160;

  if (!isMobile && tableWidth > 0) {
    const availableWidth = tableWidth - horarioColumnWidth - 24;
    if (availableWidth > 0) {
      const dynamicWidth = Math.floor(availableWidth / canchasCount);
      canchaColumnWidth = Math.max(120, dynamicWidth);
    }
  }

  // Generar columna de cancha individual con color de deporte
  const generarColumnaCancha = (cancha, colorDeporte) => ({
    title: cancha.ccl_nombre,
    key: `cancha_${cancha.ccl_id}`,
    width: canchaColumnWidth,
    render: (_, record) => {
      const layout = layoutsPorCancha[cancha.ccl_id] || {};
      const rowSpan = layout[record.horario];
      
      // Si rowSpan es 0, esta celda está oculta (parte de un bloque anterior)
      if (rowSpan === 0) {
        return {
          children: null,
          props: { rowSpan: 0 },
        };
      }
      
      // Si rowSpan es negativo, es una celda de relleno (no cabe un turno completo)
      // El valor absoluto indica cuántas filas ocupa realmente
      if (rowSpan < 0) {
        const rellenoRowSpan = Math.abs(rowSpan);
        return {
          children: (
            <div 
              style={{ 
                position: 'relative',
                minHeight: `${baseSlotHeight}px`,
                height: `${rellenoRowSpan * baseSlotHeight}px`,
                border: '1px solid #1e1e1e',
                boxSizing: 'border-box',
              }}
            />
          ),
          props: { rowSpan: rellenoRowSpan },
        };
      }
      
      const efectivoRowSpan = rowSpan || 1;
      const turnosEnCancha = getTurnosPorCancha(cancha.ccl_id);
      const turno = turnosEnCancha.find(t => esInicioTurno(record.horario, t));
      const esCeldaVacia = !turno || turno.tcl_estado === 'DISPONIBLE';
      
      return {
        children: (
          <div 
            style={{ 
              position: 'relative',
              minHeight: `${baseSlotHeight}px`,
              height: `${efectivoRowSpan * baseSlotHeight}px`,
              border: '1px solid #1e1e1e',
              padding: '0px',
              boxSizing: 'border-box',
              cursor: 'pointer',
              display: 'flex',
              alignItems: esCeldaVacia ? 'center' : undefined,
              justifyContent: esCeldaVacia ? 'center' : undefined,
            }}
            onClick={() => {
              if (!turno) {
                handleCrearTurnoDesdeCelda(cancha.ccl_id, record.horario);
              } else if (turno.tcl_estado === 'DISPONIBLE') {
                handleCrearReserva(turno);
              }
            }}
          >
            {esCeldaVacia && (
              <div style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: colorDeporte.text,
                opacity: 0.4,
              }} />
            )}
            {renderBloqueTurno(cancha.ccl_id, record.horario, colorDeporte)}
          </div>
        ),
        props: {
          rowSpan: efectivoRowSpan,
        },
      };
    },
  });

  // Paleta de colores tenues por deporte
  const coloresDeporte = [
    { bg: '#1a2332', border: '#1e3a5f', text: '#5b9bd5' },  // Azul
    { bg: '#1e2a1e', border: '#2d4a2d', text: '#6bba6b' },  // Verde
    { bg: '#2a2319', border: '#4a3d28', text: '#c4a35a' },  // Dorado
    { bg: '#2a1a2a', border: '#4a2d4a', text: '#b56bb5' },  // Violeta
    { bg: '#1a2a2a', border: '#2d4a4a', text: '#5bb5b5' },  // Teal
    { bg: '#2a1e1e', border: '#4a2d2d', text: '#c46b6b' },  // Rojo
    { bg: '#25251a', border: '#45452d', text: '#b5b55b' },  // Amarillo
    { bg: '#1e2025', border: '#2d3545', text: '#7b8db5' },  // Gris azulado
  ];

  // Agrupar canchas por deporte (ordenadas)
  const deportesVistos = {};
  canchas.forEach(cancha => {
    const deporte = cancha.tipo_deporte_nombre || 'Sin deporte';
    if (!deportesVistos[deporte]) {
      deportesVistos[deporte] = [];
    }
    deportesVistos[deporte].push(cancha);
  });

  // Generar columnas agrupadas por deporte con colores
  const deportesOrdenados = Object.entries(deportesVistos);
  const columnasDeportes = deportesOrdenados.map(([deporte, canchasDelDeporte], index) => {
    const color = coloresDeporte[index % coloresDeporte.length];
    return {
      title: (
        <div style={{ 
          background: color.bg, 
          color: color.text, 
          padding: isMobile ? '2px 4px' : '4px 8px', 
          borderRadius: 4, 
          textAlign: 'center',
          fontWeight: 600,
          fontSize: isMobile ? 10 : 13,
          border: `1px solid ${color.border}`,
          margin: isMobile ? '-2px -4px' : '-4px -8px',
          whiteSpace: 'nowrap',
        }}>
          {deporte}
        </div>
      ),
      children: canchasDelDeporte.map(c => generarColumnaCancha(c, color)),
    };
  });

  const columns = [
    {
      title: isMobile ? 'Hora' : 'HORARIO',
      dataIndex: 'horario',
      key: 'horario',
      width: horarioColumnWidth,
      fixed: 'left',
      render: (horario) => (
        <strong style={{ fontSize: isMobile ? '11px' : '13px', whiteSpace: 'nowrap' }}>{horario}</strong>
      ),
    },
    ...columnasDeportes,
  ];

  const dataSource = horarios.map(horario => ({ key: horario, horario }));

  return (
    <MainLayout>
      <Card
        title={isMobile ? "Turnos" : "Gestión de Turnos del Club"}
        extra={
          <Space size={isMobile ? 'small' : 'middle'}>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setModalConfigVisible(true)}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? '' : 'Configurar Grilla'}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadTurnos}
              loading={loading}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? '' : 'Actualizar'}
            </Button>
          </Space>
        }
        bodyStyle={isMobile ? { padding: '8px' } : undefined}
      >
        <Row gutter={[8, 8]} style={{ marginBottom: isMobile ? 8 : 16 }}>
          <Col xs={24} sm={12} md={8}>
            <DatePicker
              value={fechaSeleccionada}
              onChange={setFechaSeleccionada}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              size={isMobile ? 'small' : 'middle'}
            />
          </Col>
        </Row>

        <Spin spinning={loading}>
          <div className="turnos-club-table-wrapper" ref={tableWrapperRef}>
            <Table
              columns={columns}
              dataSource={dataSource}
              loading={loading}
              pagination={false}
              scroll={{ x: 'max-content' }}
              size="small"
              bordered={false}
            />
          </div>
        </Spin>

        {/* Modal para configurar grilla */}
        <Modal
          title="Configurar Grilla de Turnos"
          open={modalConfigVisible}
          onOk={handleSaveConfig}
          onCancel={() => setModalConfigVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 500}
        >
          <Form
            form={formConfig}
            layout="vertical"
          >
            <Form.Item
              name="horaDesde"
              label="Hora Desde"
              rules={[{ required: true, message: 'La hora de inicio es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="horaHasta"
              label="Hora Hasta"
              rules={[{ required: true, message: 'La hora de fin es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            {tiposDeporte.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontWeight: 500, marginBottom: '8px' }}>Duración del turno por deporte:</div>
                {tiposDeporte.map(tipo => (
                  <div key={tipo.tdp_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', padding: '6px 10px', background: '#1f1f1f', borderRadius: '6px' }}>
                    <span>{tipo.tdp_nombre}</span>
                    <Select
                      value={tipo.tdp_duracion_minutos || 90}
                      style={{ width: 150 }}
                      onChange={(value) => handleUpdateDuracionDeporte(tipo.tdp_id, value)}
                    >
                      <Select.Option value={30}>30 minutos</Select.Option>
                      <Select.Option value={60}>1 hora</Select.Option>
                      <Select.Option value={90}>1:30 horas</Select.Option>
                      <Select.Option value={120}>2 horas</Select.Option>
                      <Select.Option value={150}>2:30 horas</Select.Option>
                      <Select.Option value={180}>3 horas</Select.Option>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </Form>
        </Modal>

        {/* Modal para crear/editar turno o ver detalle de reserva */}
        <Modal
          title={
            editingTurno && editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id
              ? 'Detalle de Reserva'
              : editingTurno ? 'Editar Turno' : 'Nuevo Turno'
          }
          open={modalTurnoVisible}
          onOk={editingTurno && editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id ? undefined : handleSaveTurno}
          onCancel={() => {
            setModalTurnoVisible(false);
            setEditingTurno(null);
            setCeldaSeleccionada(null);
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 600}
          footer={
            editingTurno && editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id
              ? [
                  editingTurno.tcl_tipo === 'FIJO' ? (
                    <Popconfirm
                      key="no-asiste"
                      title="¿Marcar como NO ASISTE?"
                      description={`¿Está seguro de que desea liberar este turno fijo para la fecha ${fechaSeleccionada.format('DD/MM/YYYY')}? El turno seguirá activo en los demás días.`}
                      onConfirm={() => {
                        handleNoAsiste(editingTurno);
                      }}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button key="no-asiste-btn" type="default" size={isMobile ? 'small' : 'middle'} style={{ borderColor: '#faad14', color: '#faad14' }}>
                        {isMobile ? 'NO ASISTE' : 'NO ASISTE'}
                      </Button>
                    </Popconfirm>
                  ) : null,
                  <Popconfirm
                    key="delete-reserva"
                    title="¿Eliminar reserva?"
                    description="¿Está seguro de que desea eliminar esta reserva? El turno quedará disponible."
                    onConfirm={() => {
                      handleDeleteReserva(editingTurno);
                      setModalTurnoVisible(false);
                      setEditingTurno(null);
                    }}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button key="delete-reserva-btn" danger size={isMobile ? 'small' : 'middle'}>
                      {isMobile ? 'Eliminar' : 'Eliminar Reserva'}
                    </Button>
                  </Popconfirm>,
                  <Button key="close" size={isMobile ? 'small' : 'middle'} onClick={() => {
                    setModalTurnoVisible(false);
                    setEditingTurno(null);
                  }}>
                    Cerrar
                  </Button>,
                ].filter(Boolean)
              : [
                  editingTurno && editingTurno.tcl_estado === 'DISPONIBLE' ? (
                    <Button
                      key="crear-reserva"
                      type="primary"
                      size={isMobile ? 'small' : 'middle'}
                      onClick={() => {
                        handleCrearReserva(editingTurno);
                        setModalTurnoVisible(false);
                      }}
                    >
                      {isMobile ? 'Reservar' : 'Crear Reserva'}
                    </Button>
                  ) : null,
                  editingTurno && editingTurno.tcl_tipo === 'FIJO' && editingTurno.tcl_estado !== 'RESERVADO' ? (
                    <Popconfirm
                      key="no-asiste"
                      title="¿Marcar como NO ASISTE?"
                      description={`¿Está seguro de que desea liberar este turno fijo para la fecha ${fechaSeleccionada.format('DD/MM/YYYY')}? El turno seguirá activo en los demás días.`}
                      onConfirm={() => {
                        handleNoAsiste(editingTurno);
                      }}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button key="no-asiste-btn" type="default" size={isMobile ? 'small' : 'middle'} style={{ borderColor: '#faad14', color: '#faad14' }}>
                        NO ASISTE
                      </Button>
                    </Popconfirm>
                  ) : null,
                  <Button key="cancel" size={isMobile ? 'small' : 'middle'} onClick={() => {
                    setModalTurnoVisible(false);
                    setEditingTurno(null);
                    setCeldaSeleccionada(null);
                  }} disabled={guardandoTurno}>
                    Cancelar
                  </Button>,
                  <Button key="submit" type="primary" size={isMobile ? 'small' : 'middle'} onClick={handleSaveTurno} loading={guardandoTurno}>
                    Guardar
                  </Button>,
                ].filter(Boolean)
          }
        >
          {/* Vista de solo lectura para turnos reservados */}
          {editingTurno && editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id ? (
            <div>
              {/* Info del turno */}
              <div style={{ 
                border: '1px solid #3a3a3a', 
                borderRadius: 8, 
                padding: isMobile ? 12 : 16, 
                background: '#1f1f1f',
                marginBottom: isMobile ? 12 : 16
              }}>
                <div style={{ color: '#b0b0b0', fontWeight: 600, marginBottom: isMobile ? 8 : 12, fontSize: isMobile ? 12 : 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Información del Turno
                </div>
                <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 12]}>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Cancha</div>
                    <div style={{ color: '#e6e6e6', fontWeight: 500, fontSize: isMobile ? 12 : 14 }}>{editingTurno.cancha_nombre || canchas.find(c => c.ccl_id === editingTurno.tcl_cancha_club)?.ccl_nombre || '-'}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Tipo</div>
                    <div>
                      <Tag color={editingTurno.tcl_tipo === 'FIJO' ? 'orange' : 'blue'} style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>
                        {editingTurno.tcl_tipo === 'FIJO' ? (isMobile ? 'Fijo' : 'Fijo (recurrente)') : 'Único'}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Fecha</div>
                    <div style={{ color: '#e6e6e6', fontWeight: 500, fontSize: isMobile ? 12 : 14 }}>
                      {editingTurno.tcl_fecha ? dayjs(editingTurno.tcl_fecha).format('DD/MM/YYYY') : fechaSeleccionada.format('DD/MM/YYYY')}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Horario</div>
                    <div style={{ color: '#e6e6e6', fontWeight: 500, fontSize: isMobile ? 12 : 14 }}>
                      {editingTurno.tcl_hora_desde?.substring(0, 5)} - {editingTurno.tcl_hora_hasta?.substring(0, 5)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Precio</div>
                    <div style={{ color: '#52c41a', fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>
                      ${(() => {
                        const p = typeof editingTurno.tcl_precio === 'string' ? parseFloat(editingTurno.tcl_precio) : editingTurno.tcl_precio;
                        return p ? p.toFixed(2) : '0.00';
                      })()}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Estado</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Tag color="green" style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Reservado</Tag>
                      {editingTurno.tcl_tipo !== 'FIJO' && editingTurno.rcl_estado_pago && (
                        (() => {
                          const estadoPago = String(editingTurno.rcl_estado_pago || '').toLowerCase();
                          if (estadoPago === 'pagado') {
                            return <Tag color="green" style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Seña pagada</Tag>;
                          }
                          if (estadoPago === 'pendiente') {
                            return <Tag color="orange" style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Pago pendiente</Tag>;
                          }
                          return <Tag style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Sin seña</Tag>;
                        })()
                      )}
                    </div>
                  </Col>
                </Row>
                {editingTurno.tcl_observaciones && (
                  <div style={{ marginTop: isMobile ? 8 : 12, paddingTop: isMobile ? 8 : 12, borderTop: '1px solid #3a3a3a' }}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Observaciones del Turno</div>
                    <div style={{ color: '#b0b0b0', fontSize: isMobile ? 12 : 13 }}>{editingTurno.tcl_observaciones}</div>
                  </div>
                )}
                {editingTurno.rcl_observaciones && (
                  <div style={{ marginTop: isMobile ? 8 : 12, paddingTop: isMobile ? 8 : 12, borderTop: '1px solid #3a3a3a' }}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Observaciones de la Reserva</div>
                    <div style={{ color: '#b0b0b0', fontSize: isMobile ? 12 : 13 }}>{editingTurno.rcl_observaciones}</div>
                  </div>
                )}
              </div>

              {/* Info del responsable */}
              <div style={{ 
                border: '1px solid #274916', 
                borderRadius: 8, 
                padding: isMobile ? 12 : 16, 
                background: '#162312'
              }}>
                <div style={{ color: '#52c41a', fontWeight: 600, marginBottom: isMobile ? 8 : 12, fontSize: isMobile ? 12 : 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Responsable de la Reserva
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
                  <div style={{ 
                    width: isMobile ? 38 : 48, height: isMobile ? 38 : 48, borderRadius: '50%', 
                    background: '#1890ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: isMobile ? 18 : 22, flexShrink: 0
                  }}>
                    <UserOutlined />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16, color: '#e6e6e6', marginBottom: 4 }}>
                      {editingTurno.rcl_nombre_cliente || 'Sin nombre'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 4 }}>
                      {editingTurno.rcl_telefono && (
                        <div style={{ fontSize: isMobile ? 12 : 13, color: '#8c8c8c' }}>
                          <PhoneOutlined style={{ marginRight: 6 }} />{editingTurno.rcl_telefono}
                        </div>
                      )}
                      {editingTurno.rcl_email && (
                        <div style={{ fontSize: isMobile ? 12 : 13, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <MailOutlined style={{ marginRight: 6 }} />{editingTurno.rcl_email}
                        </div>
                      )}
                      {!editingTurno.rcl_telefono && !editingTurno.rcl_email && (
                        <div style={{ fontSize: isMobile ? 12 : 13, color: '#555' }}>Sin datos de contacto</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Formulario editable para turnos no reservados */
            <Form
              form={formTurno}
              layout="vertical"
              initialValues={{ tipo: 'UNICO', estado: 'DISPONIBLE' }}
            >
              <Form.Item
                name="canchaClub"
                label="Cancha"
                rules={[{ required: true, message: 'La cancha es requerida' }]}
              >
                <Select placeholder="Seleccione una cancha">
                  {canchas.map((cancha) => (
                    <Select.Option key={cancha.ccl_id} value={cancha.ccl_id}>
                      {cancha.ccl_nombre} - {cancha.tipo_deporte_nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="tipo"
                label="Tipo de Turno"
                rules={[{ required: true, message: 'El tipo de turno es requerido' }]}
              >
                <Radio.Group>
                  <Radio value="UNICO">Único (solo este día)</Radio>
                  <Radio value="FIJO">Fijo (recurrente)</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}
              >
                {({ getFieldValue }) => {
                  const tipo = getFieldValue('tipo');
                  return tipo === 'FIJO' ? (
                    <Form.Item
                      name="diaSemana"
                      label="Día de la Semana"
                      rules={[{ required: true, message: 'El día de la semana es requerido' }]}
                    >
                      <Select placeholder="Seleccione un día">
                        <Select.Option value={0}>Domingo</Select.Option>
                        <Select.Option value={1}>Lunes</Select.Option>
                        <Select.Option value={2}>Martes</Select.Option>
                        <Select.Option value={3}>Miércoles</Select.Option>
                        <Select.Option value={4}>Jueves</Select.Option>
                        <Select.Option value={5}>Viernes</Select.Option>
                        <Select.Option value={6}>Sábado</Select.Option>
                      </Select>
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="fecha"
                      label="Fecha"
                      rules={[{ required: true, message: 'La fecha es requerida' }]}
                    >
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item
                name="horaDesde"
                label="Hora Desde"
                rules={[{ required: true, message: 'La hora de inicio es requerida' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="horaHasta"
                label="Hora Hasta"
                rules={[{ required: true, message: 'La hora de fin es requerida' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="precio"
                label="Precio"
                rules={[
                  { required: true, message: 'El precio es requerido' },
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined || value === '') {
                        return Promise.reject(new Error('El precio es requerido'));
                      }
                      const precioNum = typeof value === 'string' ? parseFloat(value) : value;
                      if (isNaN(precioNum)) {
                        return Promise.reject(new Error('El precio debe ser un número válido'));
                      }
                      if (precioNum < 0) {
                        return Promise.reject(new Error('El precio debe ser mayor o igual a cero'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  prefix="$"
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>

              <Form.Item
                name="estado"
                label="Estado"
                rules={[{ required: true, message: 'El estado es requerido' }]}
              >
                <Select>
                  <Select.Option value="DISPONIBLE">Disponible</Select.Option>
                  <Select.Option value="RESERVADO">Reservado</Select.Option>
                  <Select.Option value="BLOQUEADO">Bloqueado</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="observaciones"
                label="Observaciones"
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Form>
          )}
        </Modal>

        {/* Modal para crear reserva */}
        <Modal
          title={turnoSeleccionado?.esNuevo ? "Asignar Responsable al Turno" : "Nueva Reserva"}
          open={modalReservaVisible}
          onOk={handleSaveReserva}
          confirmLoading={guardandoReserva}
          onCancel={() => {
            setModalReservaVisible(false);
            setTurnoSeleccionado(null);
            setCeldaSeleccionada(null);
            setPersonaSeleccionadaReserva(null);
            setBusquedaPersonaValor('');
            setOpcionesPersonas([]);
            setModoSinUsuario(false);
          }}
          okButtonProps={{ disabled: guardandoReserva }}
          cancelButtonProps={{ disabled: guardandoReserva }}
          okText={turnoSeleccionado?.esNuevo ? "Crear Turno y Asignar" : "Reservar"}
          cancelText="Cancelar"
          width={isMobile ? '90%' : 600}
        >
          {turnoSeleccionado && (
            <div style={{ marginBottom: isMobile ? 12 : 16, padding: isMobile ? 10 : 12, background: '#111a2c', border: '1px solid #15325b', borderRadius: 4 }}>
              <div style={{ color: '#4ea3f7', fontWeight: '600', marginBottom: '4px', fontSize: isMobile ? 13 : 14 }}>Turno:</div>
              <div style={{ color: '#7ec2f7', fontSize: isMobile ? 13 : 14 }}>
                {turnoSeleccionado.tcl_hora_desde ? turnoSeleccionado.tcl_hora_desde.substring(0, 5) : ''} - {turnoSeleccionado.tcl_hora_hasta ? turnoSeleccionado.tcl_hora_hasta.substring(0, 5) : ''}
              </div>
              <div style={{ color: '#7ec2f7', fontSize: isMobile ? 13 : 14, marginTop: '4px' }}>
                Precio: ${(() => {
                  const precioNum = typeof turnoSeleccionado.tcl_precio === 'string' 
                    ? parseFloat(turnoSeleccionado.tcl_precio) 
                    : turnoSeleccionado.tcl_precio;
                  return precioNum ? precioNum.toFixed(2) : '0.00';
                })()}
              </div>
            </div>
          )}
          
          <Form
            form={formReserva}
            layout="vertical"
            initialValues={{ tipoTurno: 'UNICO' }}
          >
            {/* Sección Responsable interactiva */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Responsable</div>
              
              {personaSeleccionadaReserva ? (
                // Tarjeta de persona seleccionada
                <div style={{ 
                  border: '1px solid #274916', 
                  borderRadius: 8, 
                  padding: isMobile ? 10 : 12, 
                  background: '#162312',
                  position: 'relative'
                }}>
                  <Button 
                    type="text" 
                    icon={<CloseCircleOutlined />} 
                    size="small"
                    onClick={handleQuitarPersona}
                    style={{ position: 'absolute', top: 6, right: 6, color: '#666' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10 }}>
                    <div style={{ 
                      width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, borderRadius: '50%', 
                      background: personaSeleccionadaReserva.tipo === 'jugador' ? '#1890ff' : '#52c41a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: isMobile ? 14 : 18, flexShrink: 0
                    }}>
                      <UserOutlined />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: isMobile ? 12 : 14, color: '#e6e6e6' }}>{personaSeleccionadaReserva.nombreCompleto}</span>
                        <Tag color={personaSeleccionadaReserva.tipo === 'jugador' ? 'blue' : 'green'} style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>
                          {personaSeleccionadaReserva.tipo === 'jugador' ? 'Jugador' : 'Alumno'}
                        </Tag>
                        <CheckCircleFilled style={{ color: '#52c41a', fontSize: isMobile ? 12 : 14 }} />
                      </div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#8c8c8c', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {personaSeleccionadaReserva.telefono && (
                          <span style={{ marginRight: 8 }}><PhoneOutlined /> {personaSeleccionadaReserva.telefono}</span>
                        )}
                        {!isMobile && personaSeleccionadaReserva.email && (
                          <span><MailOutlined /> {personaSeleccionadaReserva.email}</span>
                        )}
                      </div>
                      {isMobile && personaSeleccionadaReserva.email && (
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <MailOutlined /> {personaSeleccionadaReserva.email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : modoSinUsuario ? (
                // Aviso de jugador sin usuario
                <div style={{ 
                  border: '1px solid #594214', 
                  borderRadius: 8, 
                  padding: isMobile ? 10 : 12, 
                  background: '#2b2111',
                  position: 'relative'
                }}>
                  <Button 
                    type="text" 
                    icon={<CloseCircleOutlined />} 
                    size="small"
                    onClick={handleQuitarPersona}
                    style={{ position: 'absolute', top: 6, right: 6, color: '#666' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10 }}>
                    <WarningFilled style={{ color: '#faad14', fontSize: isMobile ? 18 : 22 }} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#faad14', fontSize: isMobile ? 12 : 13 }}>Jugador sin usuario registrado</div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#a89057' }}>Complete los datos manualmente</div>
                    </div>
                  </div>
                </div>
              ) : (
                // Estado inicial - botones para elegir
                <div style={{ 
                  border: '1px dashed #434343', 
                  borderRadius: 8, 
                  padding: isMobile ? 12 : 16, 
                  textAlign: 'center',
                  background: '#1a1a1a'
                }}>
                  <div style={{ color: '#8c8c8c', marginBottom: 10, fontSize: isMobile ? 12 : 13 }}>
                    Seleccione un jugador/alumno existente o agregue sin usuario
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button 
                      type="primary" 
                      icon={<SearchOutlined />} 
                      onClick={handleAbrirBuscarPersona}
                      size={isMobile ? 'small' : 'middle'}
                    >
                      {isMobile ? 'Buscar' : 'Buscar Jugador / Alumno'}
                    </Button>
                    <Button 
                      icon={<UserAddOutlined />} 
                      onClick={handleAgregarSinUsuario}
                      size={isMobile ? 'small' : 'middle'}
                    >
                      Sin usuario
                    </Button>
                  </div>
                </div>
              )}

              {/* Botón para cambiar selección cuando ya hay una persona o modo sin usuario */}
              {(personaSeleccionadaReserva || modoSinUsuario) && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button size="small" icon={<SearchOutlined />} onClick={handleAbrirBuscarPersona}>
                    {isMobile ? 'Cambiar' : 'Cambiar a jugador/alumno'}
                  </Button>
                  {personaSeleccionadaReserva && (
                    <Button size="small" icon={<UserAddOutlined />} onClick={handleAgregarSinUsuario}>
                      Sin usuario
                    </Button>
                  )}
                </div>
              )}
            </div>

            <>
              <Form.Item
                name="tipoTurno"
                label="Tipo de Turno"
                rules={[{ required: true, message: 'El tipo de turno es requerido' }]}
              >
                <Radio.Group>
                  <Radio value="UNICO">Único (solo este día)</Radio>
                  <Radio value="FIJO">Fijo (recurrente para este día y hora)</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.tipoTurno !== currentValues.tipoTurno}
              >
                {({ getFieldValue, setFieldsValue }) => {
                  const tipoTurno = getFieldValue('tipoTurno');
                  // Cuando cambia a FIJO, asegurar que el día de la semana esté establecido
                  if (tipoTurno === 'FIJO') {
                    const diaSemanaActual = getFieldValue('diaSemana');
                    if (diaSemanaActual === undefined || diaSemanaActual === null) {
                      // Si no hay día seleccionado, usar el día de la fecha donde se hizo clic
                      const diaSemana = fechaSeleccionada.day();
                      setFieldsValue({ diaSemana });
                    }
                  }
                  return tipoTurno === 'FIJO' ? (
                    <Form.Item
                      name="diaSemana"
                      label="Día de la Semana"
                      rules={[{ required: true, message: 'El día de la semana es requerido' }]}
                    >
                      <Select placeholder="Seleccione un día">
                        <Select.Option value={0}>Domingo</Select.Option>
                        <Select.Option value={1}>Lunes</Select.Option>
                        <Select.Option value={2}>Martes</Select.Option>
                        <Select.Option value={3}>Miércoles</Select.Option>
                        <Select.Option value={4}>Jueves</Select.Option>
                        <Select.Option value={5}>Viernes</Select.Option>
                        <Select.Option value={6}>Sábado</Select.Option>
                      </Select>
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
            </>

            <Form.Item
              name="nombreCliente"
              label={turnoSeleccionado?.esNuevo ? "Responsable" : "Nombre del Cliente"}
              rules={[{ required: true, message: turnoSeleccionado?.esNuevo ? 'El responsable es requerido' : 'El nombre del cliente es requerido' }]}
            >
              <Input placeholder={turnoSeleccionado?.esNuevo ? "Nombre del responsable (ej: Juan Pablo)" : "Nombre completo"} />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono"
            >
              <Input placeholder="Teléfono de contacto" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Ingrese un email válido' }]}
            >
              <Input placeholder="Email del cliente" />
            </Form.Item>

            <Form.Item
              name="observaciones"
              label="Observaciones"
            >
              <Input.TextArea rows={3} placeholder="Notas adicionales sobre la reserva" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal de búsqueda de jugadores/alumnos */}
        <Modal
          title={isMobile ? "Buscar Jugador" : "Buscar Jugador o Alumno"}
          open={modalBuscarPersonaVisible}
          onCancel={() => {
            setModalBuscarPersonaVisible(false);
            setBusquedaPersonaValor('');
            setOpcionesPersonas([]);
          }}
          footer={[
            <Button key="sinusuario" icon={<UserAddOutlined />} onClick={handleAgregarSinUsuario} size={isMobile ? 'small' : 'middle'}>
              {isMobile ? 'Sin usuario' : 'Agregar sin usuario'}
            </Button>,
            <Button key="cancel" size={isMobile ? 'small' : 'middle'} onClick={() => {
              setModalBuscarPersonaVisible(false);
              setBusquedaPersonaValor('');
              setOpcionesPersonas([]);
            }}>
              Cancelar
            </Button>,
          ]}
          width={isMobile ? '95%' : 520}
          styles={{ body: { padding: isMobile ? '10px 12px' : '12px 24px', maxHeight: '60vh', overflowY: 'auto' } }}
        >
          <Input
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder={isMobile ? "Nombre, teléfono o email..." : "Escriba el nombre, teléfono o email..."}
            value={busquedaPersonaValor}
            onChange={(e) => {
              setBusquedaPersonaValor(e.target.value);
              buscarPersonasReserva(e.target.value);
            }}
            allowClear
            size={isMobile ? 'middle' : 'large'}
            style={{ marginBottom: isMobile ? 12 : 16 }}
            autoFocus
          />

          {buscandoPersonas ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <Spin tip="Buscando..." />
            </div>
          ) : opcionesPersonas.length > 0 ? (
            <>
              {opcionesPersonas.length > PERSONAS_POR_PAGINA && (
                <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
                  {opcionesPersonas.length} resultados encontrados
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {opcionesPersonas
                  .slice((paginaPersonas - 1) * PERSONAS_POR_PAGINA, paginaPersonas * PERSONAS_POR_PAGINA)
                  .map((persona) => (
                  <div
                    key={`${persona.tipo}-${persona.id}`}
                    onClick={() => handleSelectPersonaReserva(persona)}
                    style={{
                      border: '1px solid #303030',
                      borderRadius: 8,
                      padding: isMobile ? 10 : 12,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 8 : 12,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = persona.tipo === 'jugador' ? '#1890ff' : '#52c41a';
                      e.currentTarget.style.background = persona.tipo === 'jugador' ? '#111a2c' : '#162312';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#303030';
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ 
                      width: isMobile ? 36 : 44, height: isMobile ? 36 : 44, borderRadius: '50%', 
                      background: persona.tipo === 'jugador' ? '#1890ff' : '#52c41a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: isMobile ? 16 : 20, flexShrink: 0
                    }}>
                      <UserOutlined />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: isMobile ? 12 : 14, color: '#e6e6e6' }}>{persona.nombreCompleto}</span>
                        <Tag 
                          color={persona.tipo === 'jugador' ? 'blue' : 'green'} 
                          style={{ margin: 0, fontSize: isMobile ? 10 : 11 }}
                        >
                          {persona.tipo === 'jugador' ? 'Jugador' : 'Alumno'}
                        </Tag>
                      </div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#8c8c8c', display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: isMobile ? 2 : 8 }}>
                        {persona.telefono && (
                          <span><PhoneOutlined style={{ marginRight: 3 }} />{persona.telefono}</span>
                        )}
                        {persona.email && (
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><MailOutlined style={{ marginRight: 3 }} />{persona.email}</span>
                        )}
                        {!persona.telefono && !persona.email && (
                          <span style={{ color: '#555' }}>Sin datos de contacto</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {opcionesPersonas.length > PERSONAS_POR_PAGINA && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                  <Pagination
                    current={paginaPersonas}
                    total={opcionesPersonas.length}
                    pageSize={PERSONAS_POR_PAGINA}
                    onChange={(page) => setPaginaPersonas(page)}
                    size="small"
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          ) : busquedaPersonaValor && busquedaPersonaValor.length >= 2 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#666' }}>
              <UserOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
              <div>No se encontraron resultados</div>
              <div style={{ fontSize: 12, marginTop: 4, color: '#555' }}>
                Puede agregar como jugador sin usuario
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#555' }}>
              <SearchOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
              <div>Escriba al menos 2 caracteres para buscar</div>
            </div>
          )}
        </Modal>
      </Card>
    </MainLayout>
  );
};

export default TurnosClub;
